import conversationsMd from './conversations.md?raw';
import workflowsMd from './workflows.md?raw';
import templatesMd from './templates.md?raw';
import toolsMd from './tools.md?raw';
import runsMd from './runs.md?raw';
import decisionsMd from './decisions.md?raw';
import promptsMd from './prompts.md?raw';
import intelSourcesMd from './intel-sources.md?raw';
import accountPersonasMd from './account-personas.md?raw';
import mlInsightsMd from './ml-insights.md?raw';
import activeAgentsMd from './active-agents.md?raw';
import strategiesMd from './strategies.md?raw';
import testerMd from './tester.md?raw';
import portfolioMd from './portfolio.md?raw';
import watchlistMd from './watchlist.md?raw';
import walletsMd from './wallets.md?raw';
import portfoliosMd from './portfolios.md?raw';
import tradingMd from './trading.md?raw';
import platformConnectionsMd from './platform-connections.md?raw';
import analysisTemplatesMd from './analysis-templates.md?raw';
import mcpConnectionsMd from './mcp-connections.md?raw';
import toolLibrariesMd from './tool-libraries.md?raw';
import daemonsMd from './daemons.md?raw';
import qualityDashboardMd from './quality-dashboard.md?raw';
import reasoningEvaluationsMd from './reasoning-evaluations.md?raw';
import memoriesMd from './memories.md?raw';
import artifactsMd from './artifacts.md?raw';
import projectsMd from './projects.md?raw';
import teamsMd from './teams.md?raw';
import tasksMd from './tasks.md?raw';
import workforceMd from './workforce.md?raw';
import promotionCandidatesMd from './promotion-candidates.md?raw';
import runtimeFlagsMd from './runtime-flags.md?raw';
import projectCoordinationMd from './project-coordination.md?raw';
import projectChangesMd from './project-changes.md?raw';
import projectLogMd from './project-log.md?raw';
import conversationIntentMd from './conversation-intent.md?raw';
import conversationFlowMd from './conversation-flow.md?raw';
import conversationRecordsMd from './conversation-records.md?raw';
import workerSdkMd from './worker-sdk.md?raw';
import apiKeysMd from './api-keys.md?raw';
import organizationsMd from './organizations.md?raw';
import indicatorsMd from './indicators.md?raw';
import evaluationTemplatesMd from './evaluation-templates.md?raw';
import runMonitoringMd from './run-monitoring.md?raw';
import executionAlgosMd from './execution-algos.md?raw';
import dashboardsMd from './dashboards.md?raw';
import improvementsMd from './improvements.md?raw';
import marketDataIngestionMd from './market-data-ingestion.md?raw';
import simpleLandingMd from './simple-landing.md?raw';
import marketplaceNeedsSetupMd from './marketplace-needs-setup.md?raw';
import marketplaceUpdatesMd from './marketplace-updates.md?raw';
import usersMd from './users.md?raw';
import systemInfoMd from './system-info.md?raw';
import systemHostMd from './system-host.md?raw';
import systemDatabaseMd from './system-database.md?raw';
import observabilityMd from './observability.md?raw';
import actionRequestsMd from './action-requests.md?raw';
import systemOverviewMd from './system-overview.md?raw';
import entitySummariesMd from './entity-summaries.md?raw';

export interface LearnItem {
  question: string;
  answer: string;
}

export interface PageHelp {
  title: string;
  summary: string;
  markdown: string;
  emptyState: {
    title: string;
    description: string;
    learnItems: LearnItem[];
  };
}

function parseHelpFile(raw: string): PageHelp {
  const fmMatch = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!fmMatch) {
    return { title: '', summary: '', markdown: raw, emptyState: { title: '', description: '', learnItems: [] } };
  }

  const frontmatter = fmMatch[1];
  const body = fmMatch[2].trim();

  const getString = (key: string): string => {
    const m = frontmatter.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
    return m ? m[1].trim() : '';
  };

  // Parse learnItems from YAML-like format
  const learnItems: LearnItem[] = [];
  const itemsMatch = frontmatter.match(/learnItems:\n([\s\S]*?)(?=\n\w|\n---|\s*$)/);
  if (itemsMatch) {
    const lines = itemsMatch[1].split('\n');
    let current: Partial<LearnItem> = {};
    for (const line of lines) {
      const qMatch = line.match(/^\s+-\s+question:\s*(.+)/);
      const aMatch = line.match(/^\s+answer:\s*(.+)/);
      if (qMatch) {
        if (current.question && current.answer) learnItems.push(current as LearnItem);
        current = { question: qMatch[1].trim() };
      } else if (aMatch) {
        current.answer = aMatch[1].trim();
      }
    }
    if (current.question && current.answer) learnItems.push(current as LearnItem);
  }

  return {
    title: getString('title'),
    summary: getString('summary'),
    markdown: body,
    emptyState: {
      title: getString('emptyStateTitle'),
      description: getString('emptyStateDescription'),
      learnItems,
    },
  };
}

export const PAGE_HELP: Record<string, PageHelp> = {
  'simple-landing': parseHelpFile(simpleLandingMd),
  workflows: parseHelpFile(workflowsMd),
  templates: parseHelpFile(templatesMd),
  tools: parseHelpFile(toolsMd),
  runs: parseHelpFile(runsMd),
  decisions: parseHelpFile(decisionsMd),
  prompts: parseHelpFile(promptsMd),
  'intel-sources': parseHelpFile(intelSourcesMd),
  'account-personas': parseHelpFile(accountPersonasMd),
  'ml-insights': parseHelpFile(mlInsightsMd),
  'active-agents': parseHelpFile(activeAgentsMd),
  strategies: parseHelpFile(strategiesMd),
  tester: parseHelpFile(testerMd),
  portfolio: parseHelpFile(portfolioMd),
  watchlist: parseHelpFile(watchlistMd),
  wallets: parseHelpFile(walletsMd),
  portfolios: parseHelpFile(portfoliosMd),
  trading: parseHelpFile(tradingMd),
  'platform-connections': parseHelpFile(platformConnectionsMd),
  'analysis-templates': parseHelpFile(analysisTemplatesMd),
  'mcp-connections': parseHelpFile(mcpConnectionsMd),
  'skill-libraries': parseHelpFile(toolLibrariesMd),
  daemons: parseHelpFile(daemonsMd),
  'quality-dashboard': parseHelpFile(qualityDashboardMd),
  'reasoning-evaluations': parseHelpFile(reasoningEvaluationsMd),
  conversations: parseHelpFile(conversationsMd),
  memories: parseHelpFile(memoriesMd),
  artifacts: parseHelpFile(artifactsMd),
  projects: parseHelpFile(projectsMd),
  teams: parseHelpFile(teamsMd),
  tasks: parseHelpFile(tasksMd),
  workforce: parseHelpFile(workforceMd),
  'promotion-candidates': parseHelpFile(promotionCandidatesMd),
  'runtime-flags': parseHelpFile(runtimeFlagsMd),
  'project-coordination': parseHelpFile(projectCoordinationMd),
  'project-changes': parseHelpFile(projectChangesMd),
  'project-log': parseHelpFile(projectLogMd),
  'conversation-intent': parseHelpFile(conversationIntentMd),
  'conversation-flow': parseHelpFile(conversationFlowMd),
  'conversation-records': parseHelpFile(conversationRecordsMd),
  'worker-sdk': parseHelpFile(workerSdkMd),
  'api-keys': parseHelpFile(apiKeysMd),
  organizations: parseHelpFile(organizationsMd),
  indicators: parseHelpFile(indicatorsMd),
  'evaluation-templates': parseHelpFile(evaluationTemplatesMd),
  'run-monitoring': parseHelpFile(runMonitoringMd),
  'execution-algos': parseHelpFile(executionAlgosMd),
  dashboards: parseHelpFile(dashboardsMd),
  improvements: parseHelpFile(improvementsMd),
  'market-data-ingestion': parseHelpFile(marketDataIngestionMd),
  'marketplace-needs-setup': parseHelpFile(marketplaceNeedsSetupMd),
  'marketplace-updates': parseHelpFile(marketplaceUpdatesMd),
  users: parseHelpFile(usersMd),
  'system-info': parseHelpFile(systemInfoMd),
  'system-host': parseHelpFile(systemHostMd),
  'system-database': parseHelpFile(systemDatabaseMd),
  observability: parseHelpFile(observabilityMd),
  'action-requests': parseHelpFile(actionRequestsMd),
  'system-overview': parseHelpFile(systemOverviewMd),
  'entity-summaries': parseHelpFile(entitySummariesMd),
};
